"use client";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// --- Animation Variants ---
// Kept only for the Hero Section and Cookie Banner

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const sentenceVariant = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.08,
    },
  },
};

const wordVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } },
};

const fadeInUp = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } },
};

const bannerSlideIn = {
  hidden: { y: "100%", opacity: 0 },
  visible: { y: "0%", opacity: 1, transition: { type: "spring", stiffness: 100, damping: 20 } },
  exit: { y: "100%", opacity: 0, transition: { duration: 0.3, ease: "easeIn" } },
};

export default function CookiePolicy() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem("cookieConsent", "declined");
    setShowBanner(false);
  };
  
  const sections = [
    { id: "what-are-cookies", title: "1. What Are Cookies", content: <p>Cookies are small text files placed on your device by a website. They allow websites to recognize your device, remember preferences, and improve your experience.</p> },
    { id: "why-we-use-cookies", title: "2. Why We Use Cookies", content: <p>We use cookies to operate our platform effectively, analyze performance, deliver relevant content, and remember your preferences. Certain cookies are necessary for the platform to function securely.</p> },
    { id: "types-of-cookies", title: "3. Types of Cookies", content: <>
      <p>We use the following types of cookies:</p>
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        {[{title: "Essential Cookies", text: "Enable core functionality like security and accessibility."}, {title: "Analytics Cookies", text: "Help us understand usage and improve user experience."}, {title: "Preference Cookies", text: "Remember choices such as language or region."}, {title: "Marketing Cookies", text: "Track visitor behavior for targeted advertising."}].map(card => (
          <div key={card.title} className="p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-[#16a085]">{card.title}</h3>
            <p className="mt-2 text-sm">{card.text}</p>
          </div>
        ))}
      </div>
    </> },
    { id: "managing-cookie-preferences", title: "4. Managing Cookie Preferences", content: <p>You can manage cookie preferences via your browser settings. You may disable all or certain cookies, but doing so may impact functionality. For further customization, use our cookie banner at the bottom of the page.</p> },
    { id: "consent-and-changes", title: "5. Consent and Changes", content: <>
      <p>By clicking “Accept,” you consent to our use of cookies. You can change or withdraw your consent at any time. We may update this policy to reflect changes in technology or law.</p>
      <p className="mt-4 text-sm text-gray-500">Last updated: January 2025</p>
    </> },
    { id: "contact-us", title: "6. Contact Us", content: <>
      <p>If you have any questions about this Cookie Policy, please contact us:</p>
      <div className="mt-6 space-y-4">
        <div className="flex items-start">
          <div className="flex-shrink-0"><svg className="h-6 w-6 text-[#16a085]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div>
          <div className="ml-3"><p className="text-sm font-medium text-gray-900">Email</p><a href="mailto:contact@shigjeta.com" className="text-sm text-[#16a085] hover:underline">contact@shigjeta.com</a></div>
        </div>
        <div className="flex items-start">
          <div className="flex-shrink-0"><svg className="h-6 w-6 text-[#16a085]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg></div>
          <div className="ml-3"><p className="text-sm font-medium text-gray-900">Address</p><p className="text-sm text-gray-700">Void Support System HQ, Kacanik, Kosovo</p></div>
        </div>
      </div>
    </> },
  ];
  
  const navLinks = sections.map(section => ({
      href: `#${section.id}`,
      text: section.title.substring(section.title.indexOf(' ') + 1) // "1. Title" -> "Title"
  }));


  return (
    <main className="bg-white text-gray-900 font-sans antialiased overflow-x-hidden ">
      {/* Hero Section with Animations */}
      <section className="relative flex min-h-[40vh] sm:min-h-[50vh] items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white overflow-hidden pt-20">
        <div className="absolute inset-0 z-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1400 400" preserveAspectRatio="xMidYMid slice">
            <path fill="#16a085" d="M0,400L80,373.3C160,347,320,293,480,280C640,267,800,293,960,306.7C1120,320,1280,320,1360,320L1400,320L1400,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z" />
          </svg>
        </div>
        <motion.div
          className="relative z-10 max-w-7xl mx-auto text-center"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight"
            variants={sentenceVariant}
          >
            <span className="block">
              {"Cookie Policy".split(" ").map((word, i) => <motion.span key={i} className="inline-block" variants={wordVariant}>{word} </motion.span>)}
            </span>
            <span className="block text-[#16a085] mt-2">
              {"Your Privacy, Our Priority".split(" ").map((word, i) => <motion.span key={i} className="inline-block" variants={wordVariant}>{word} </motion.span>)}
            </span>
          </motion.h1>
          <motion.p
            className="mt-4 max-w-3xl mx-auto text-base sm:text-lg text-gray-600 leading-relaxed"
            variants={fadeInUp}
          >
            We use cookies to enhance your experience on our platform. This policy explains how we use cookies and how you can manage them.
          </motion.p>
        </motion.div>
      </section>

      {/* Cookie Policy Content (Static - No Animations) */}
      <div className="max-w-7xl xl:max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          
          <aside className="lg:w-64 flex-shrink-0 lg:sticky lg:top-24 self-start">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Table of Contents
            </h2>
            <div className="lg:hidden flex overflow-x-auto scrollbar-hide -mx-4 px-4 pb-4 space-x-2">
              {navLinks.map((link) => (
                <a key={link.href} href={link.href} className="block flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#16a085] bg-gray-50 hover:bg-gray-100 rounded-md transition-colors duration-150">
                  {link.text}
                </a>
              ))}
            </div>
            <ul className="hidden lg:block space-y-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-[#16a085] hover:bg-gray-50 rounded-md transition-colors duration-150">
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <div className="lg:flex-1 min-w-0">
            <div className="prose prose-lg max-w-none text-gray-700">
              {sections.map(section => (
                <section key={section.id} id={section.id} className="mb-16 scroll-mt-24">
                  <h2 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-4 mb-6">{section.title}</h2>
                  {section.content}
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showBanner && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-4 z-50"
            variants={bannerSlideIn}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-center md:text-left text-gray-700 font-sans">
                We use cookies to improve your experience. By continuing to browse, you agree to our use of cookies.
              </p>
              <div className="flex gap-3 flex-shrink-0">
                <motion.button
                  onClick={declineCookies}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100 font-sans"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Decline
                </motion.button>
                <motion.button
                  onClick={acceptCookies}
                  className="px-4 py-2 text-sm bg-[#16a085] text-white rounded hover:bg-[#12876f] font-sans"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Accept
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}